import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { verifyTokenApi } from '../api/auth/verify-token';
import { JobApplication } from '../types/jobs';
import { getCurrentMonthFirstDate, getCurrentMonthLastDate } from '../utils';
interface AuthState {
  token: string | null;
  name: string | null;
  role: string | null;
  account: string | null;
  accountType: AccountType | null;
  permissions: string[];
  isAuthenticated: boolean;
  dateRange: {
    startDate: string;
    endDate: string;
    key: string;
  }[];
}

enum AccountType {
  KIMCRM = 'kimcrm',
  NON_KIMCRM = 'non-kimcrm',
}

const initialState: AuthState = {
  token: null,
  name: null,
  role: null,
  account: null,
  accountType: null,
  permissions: [],
  isAuthenticated: false,
  dateRange: [
    {
      startDate: getCurrentMonthFirstDate(),
      endDate: getCurrentMonthLastDate(),
      key: 'date-range',
    }
  ]
};

interface AuthStore extends AuthState {
  updateAuthState: (data: AuthState) => void;
  logout: () => void;
  isTokenExpired: () => Promise<boolean>;
  setDateRange: (range: any) => void;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      ...initialState,
      updateAuthState: (data: Partial<AuthState>) =>
        set((state) => ({
          ...state,
          ...data,
        })),
      logout: () => set(initialState),
      isTokenExpired: async () => {
        const token = get().token;
        if (!token) return true;

        try {
          const response = await verifyTokenApi();
          if (!response.valid) {
            get().logout();
            return true;
          }

          return false;
        } catch {
          get().logout();
          return true;
        }
      },
      getPermissions: () => {
        return get().permissions;
      },
      setDateRange: (range: any) => set({ dateRange: range }),
    }),
    {
      name: 'auth',
    }
  )
);

interface AgentAuthState {
  agent: JobApplication | null;
  isOnboarded: boolean;
}

const initialAgentState: AgentAuthState = {
  agent: null,
  isOnboarded: false,
};

interface AgentAuthStore extends AgentAuthState {
  updateAgentAuthState: (data: Partial<AgentAuthState>) => void;
  setIsOnboarded: (isOnboarded: boolean) => void;
}

export const useAgentAuthStore = create<AgentAuthStore>()(
  persist(
    (set, get) => ({
      ...initialAgentState,
      updateAgentAuthState: (data: Partial<AgentAuthState>) =>
        set((state) => ({
          ...state,
          ...data,
        })),
      setIsOnboarded: (isOnboarded: boolean) =>
        set((state) => ({
          ...state,
          isOnboarded,
        })),
    }),
    {
      name: 'agent-auth',
    }
  )
);
